import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { FeatureFlags } from '../../../common/constants/featureFlags';
import { SharedConstants } from '../../../common/constants/shared';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { MibCodebackRow, MibCodebackRowHandle } from './MibCodebackRow';
import { deleteDraft } from './notesDraftSlice';
import styles from './notesDrawer.module.scss';

const CHAR_WARN_THRESHOLD = 4500;
const CHAR_DANGER_THRESHOLD = 4900;
const SAVE_INDICATOR_TICK_MS = 30_000;

// Compact relative-time format that matches the HTML mock's save indicator
// (e.g. "just now", "2m ago", "3h ago"). Re-evaluated on every render so the tick effect
// below keeps it fresh while the user is idle.
const formatRelative = (isoString: string): string => {
  const elapsedMs = Date.now() - new Date(isoString).getTime();
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000));
  if (seconds < 30) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

interface DraftColumnProps {
  applicationId: string;
  content: string;
  onChange: (next: string) => void;
  mibCodes: string[];
  onMibCodesChange: (next: string[]) => void;
  onSubmit: () => void;
  onPopOutToggle?: () => void;
  isPoppedOut?: boolean;
}

export const DraftColumn: React.FC<DraftColumnProps> = ({
  applicationId,
  content,
  onChange,
  mibCodes,
  onMibCodesChange,
  onSubmit,
  onPopOutToggle,
  isPoppedOut,
}) => {
  const { flags } = useContext(FeatureFlagsContext);
  const isMibCodebackDraftEnabled =
    flags[FeatureFlags.UW_NORA_MIB_CODEBACK_DRAFT_ENABLED];

  const mibRowRef = useRef<MibCodebackRowHandle>(null);

  const dispatch = useDispatch<AppDispatch>();
  const {
    isLoading,
    isSaving,
    isDeleting,
    saveError,
    lastSavedAt,
    authorName,
  } = useSelector((state: RootState) => state.notesDraft);

  // Force a re-render every 30s so "Saved Xm ago" stays accurate while the user is idle.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return undefined;
    const id = setInterval(() => setTick(t => t + 1), SAVE_INDICATOR_TICK_MS);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  const length = content.length;
  const overLimit = length > SharedConstants.CHAR_LIMITS;
  const charColor =
    length >= CHAR_DANGER_THRESHOLD
      ? 'error.main'
      : length >= CHAR_WARN_THRESHOLD
        ? 'warning.main'
        : 'text.secondary';

  const handleDelete = async () => {
    const ok = window.confirm(
      'This will permanently delete your draft. Submitted notes are not affected.',
    );
    if (!ok) {
      return;
    }
    const deleted = await dispatch(deleteDraft(applicationId));
    if (!deleted) {
      return;
    }
    onChange('');
    onMibCodesChange([]);
  };

  const handleSubmit = () => {
    if (overLimit || !content.trim()) {
      return;
    }
    if (isMibCodebackDraftEnabled) {
      const pending = mibRowRef.current?.getPendingInput()?.trim() ?? '';
      if (pending) {
        const ok = window.confirm(
          'If you continue, the unconfirmed codeback will be discarded.',
        );
        if (!ok) return;
        mibRowRef.current?.clearPending();
      }
    }
    onSubmit();
  };

  const renderSaveStatus = () => {
    if (isLoading) {
      return (
        <Typography variant="caption" color="text.secondary">
          Loading draft…
        </Typography>
      );
    }
    if (saveError) {
      return (
        <Typography variant="caption" color="error.main">
          {saveError}
        </Typography>
      );
    }
    if (isDeleting) {
      return (
        <Typography variant="caption" color="text.secondary">
          Deleting…
        </Typography>
      );
    }
    if (isSaving) {
      return (
        <Typography variant="caption" color="text.secondary">
          Saving…
        </Typography>
      );
    }
    if (lastSavedAt) {
      // Mock format: "Saved just now · A. Mehatre", aging to "Saved 2m ago · A. Mehatre".
      const when = formatRelative(lastSavedAt);
      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box className={styles.SaveDot} />
          <Typography variant="caption" color="text.secondary">
            Saved {when}
            {authorName ? ` · ${authorName}` : ''}
          </Typography>
        </Box>
      );
    }
    return <Box />;
  };

  return (
    <Box className={styles.DraftColumn}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="caption" fontWeight={500} color="text.secondary">
          Draft
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {renderSaveStatus()}
          {onPopOutToggle && (
            <Tooltip title={isPoppedOut ? 'Pop in' : 'Pop out'}>
              <IconButton size="small" onClick={onPopOutToggle}>
                <FontAwesomeIcon icon={faExternalLink} fontSize={14} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TextField
        multiline
        fullWidth
        variant="outlined"
        placeholder="Start typing your case notes…"
        value={content}
        disabled={isLoading || isDeleting}
        onChange={event => onChange(event.target.value)}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            height: '100%',
            padding: '10px 12px',
            fontSize: 13,
            lineHeight: 1.55,
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': {
              border: '0.5px solid rgba(0,0,0,0.22)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: '0.5px solid rgba(0,0,0,0.22)',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 2px rgba(127, 119, 221, 0.15)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #7f77dd',
              },
            },
          },
          '& textarea': {
            height: '100% !important',
            overflowY: 'auto !important',
            overscrollBehavior: 'contain',
          },
        }}
      />

      {isMibCodebackDraftEnabled && (
        <MibCodebackRow
          ref={mibRowRef}
          value={mibCodes}
          onChange={onMibCodesChange}
        />
      )}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={1}
      >
        <Typography variant="caption" color={charColor}>
          {length} / {SharedConstants.CHAR_LIMITS}
        </Typography>
        <Box display="flex" gap={0.75}>
          <Button
            disableRipple
            disabled={isLoading || isDeleting || isSaving}
            onClick={handleDelete}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 400,
              color: '#6b6b67',
              bgcolor: 'transparent',
              border: '0.5px solid rgba(0,0,0,0.22)',
              borderRadius: '8px',
              height: 28,
              minHeight: 0,
              minWidth: 0,
              padding: '0 12px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: '#f5f5f3',
                color: '#a32d2d',
                borderColor: '#e24b4a',
              },
            }}
          >
            Delete draft
          </Button>
          <Button
            disableRipple
            disabled={
              isLoading ||
              isDeleting ||
              overLimit ||
              !content.trim() ||
              isSaving
            }
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 500,
              color: '#ffffff',
              bgcolor: '#7f77dd',
              border: '0.5px solid #7f77dd',
              borderRadius: '8px',
              height: 28,
              minHeight: 0,
              minWidth: 0,
              padding: '0 16px',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              '&:hover': {
                bgcolor: '#534ab7',
                borderColor: '#534ab7',
              },
              '&.Mui-disabled': {
                opacity: 0.45,
                color: '#ffffff',
                bgcolor: '#7f77dd',
                borderColor: '#7f77dd',
              },
            }}
          >
            Submit note
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
